<template>
  <div class="base-modal" @keydown.esc="closeModal">
    <div
      ref="modal"
      v-on-click-outside="closeOnClickOutsideHandler"
      class="base-modal__modal"
      :aria-label="modalName + ' - dialog'"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <div class="base-modal__wrapper">
        <h2 v-if="title" class="base-modal__title font-l">
          {{ title }}
        </h2>
        <div class="base-modal__content">
          <slot></slot>
          <div v-if="showCloseButton" class="base-modal__close">
            <base-button :label="closeModalLabel" type="button" @click="closeModal"></base-button>
          </div>
        </div>
        <button
          v-if="showXButton"
          ref="closeXBtn"
          class="base-modal__x-btn"
          :aria-label="closeModalLabel"
          type="button"
          @click="closeModal"
        >
          <i class="icon icon-Close" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watchEffect, onBeforeUnmount } from 'vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useI18n } from 'vue-i18n';
import BaseButton from '../../2_elements/BaseButton/BaseButton.vue';
import { useAppStore } from '../../../stores/appStore';
import { vOnClickOutside } from '@vueuse/components';

const { t } = useI18n();
const appStore = useAppStore();

const emit = defineEmits(['closeModal']);

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  modalName: {
    type: String,
    default: 'Modal',
  },
  closeBtnText: {
    type: String,
  },
  showCloseButton: {
    type: Boolean,
    default: false,
  },
  showXButton: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
  },
  closeOnClickOutside: {
    type: Boolean,
    default: false,
  },
});

const modal = ref(null);
const closeXBtn = ref(null);

const initialFocusElement = computed(() => {
  if (props.showXButton && closeXBtn.value) {
    return closeXBtn.value;
  }
  return modal.value;
});

const { deactivate } = useFocusTrap(modal, {
  allowOutsideClick: true,
  immediate: true,
  initialFocus: () => initialFocusElement.value,
});

const closeModalLabel = computed(() => {
  return props.closeBtnText ? props.closeBtnText : t('close');
});

const closeModal = () => {
  emit('closeModal');
};

const closeOnClickOutsideHandler = computed(() => {
  return [props.closeOnClickOutside ? closeModal : () => null];
});

watchEffect(() => {
  appStore.setBackdrop({ enable: props.isVisible, global: true });
});

onBeforeUnmount(() => {
  deactivate();
  appStore.unblockScrolling();
  appStore.setBackdrop({ enable: false, global: true });
});

onMounted(() => {
  appStore.blockScrolling();
});
</script>

<style lang="scss">
@import './scss/BaseModal.scss';
</style>
